import { IconSparkles } from "@tabler/icons-react"
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
	isLegacyStudentEmailContent,
	type LegacyStudentEmailContent,
	type StudentEmailContent,
	type StudentEmailEditorDocument,
} from "@forento/core/models/studentEmail"
import { replacePlaceholders } from "@forento/core/utilities/string"
import { type StudentEmailEditorValue } from "@forento/ui/components/StudentEmailEditor"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import AiStudentEmailModal, { type AiStudentEmailTarget } from "~/components/AiStudentEmailModal"
import Button from "~/components/Button"
import CodeEditor from "~/components/CodeEditor"
import EmailPreview, { type EmailPreviewContent } from "~/components/EmailPreview"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalText, ModalTitle } from "~/components/Modal"
import RadioButtons from "~/components/RadioButtons"
import StudentEmailEditor from "~/components/StudentEmailEditor"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

const options = [
	{ id: "default", label: "Use default content" },
	{ id: "custom", label: "Use custom content" },
] as const
type OptionId = (typeof options)[number]["id"]

export type EmailContent = { title: string; content: EmailPreviewContent }
export type EmailSaveData = { title: string; content: StudentEmailEditorValue } | null

type Email = {
	custom: { title: string; content: StudentEmailContent | null } | null
	default?: { title: string; document: StudentEmailEditorDocument; html: string }
}
type Props = {
	email: Email
	placeholders: { key: string; value: string }[]
	layout: string
	/** Its presence is what offers writing the email with Forento AI. */
	aiTarget?: AiStudentEmailTarget
	onSave(data: EmailSaveData): Promise<void>
}
const EditStudentEmail: FC<Props> = ({ email, placeholders, layout, aiTarget, onSave }) => {
	const platform = usePlatform()!
	const alert = useAlert()
	const toast = useToast()

	const [isPreviewOpen, setIsPreviewOpen] = useState(false)

	const savedContent = email.custom?.content ?? null
	const legacyContent: LegacyStudentEmailContent | null =
		savedContent && isLegacyStudentEmailContent(savedContent) ? savedContent : null
	// HTML is not stored any more, so the editor reserializes on mount and fills this in.
	const savedEditorValue: StudentEmailEditorValue | null =
		savedContent?.type === "editor" ? { document: savedContent.document, html: "" } : null

	// An email without a default, such as a product access email, has nothing to fall back to.
	const savedOptionId: OptionId = email.custom || !email.default ? "custom" : "default"

	const [selectedOptionId, setSelectedOptionId] = useState<OptionId>(savedOptionId)
	const [hasSwitchedFromLegacy, setHasSwitchedFromLegacy] = useState(false)
	const [customTitle, setCustomTitle] = useState(email.custom?.title ?? email.default?.title ?? "")
	const [editorValue, setEditorValue] = useState<StudentEmailEditorValue | null>(null)
	const [isEditorDirty, setIsEditorDirty] = useState(false)
	const editorRevision = useRef(0)
	const [isAiModalOpen, setIsAiModalOpen] = useState(false)
	// The generation id keys the editor so each generated draft remounts with its new document.
	const [generated, setGenerated] = useState<{ id: number; document: StudentEmailEditorDocument } | null>(null)

	const isLegacyLocked = legacyContent !== null && !hasSwitchedFromLegacy
	// Falls back to what is stored so that changing only the subject still has content to save.
	const effectiveValue = editorValue ?? (hasSwitchedFromLegacy || generated ? null : savedEditorValue)

	// Choosing custom content opens the default email itself. Switching away from legacy content deliberately
	// starts clean, since the old email is still on screen to copy from.
	const initialDocument = generated
		? generated.document
		: hasSwitchedFromLegacy
			? null
			: (savedEditorValue?.document ?? email.default?.document ?? null)

	const handleEditorChange = useCallback((value: StudentEmailEditorValue, isUserEdit: boolean) => {
		setEditorValue(value)
		if (isUserEdit) {
			editorRevision.current += 1
			setIsEditorDirty(true)
		}
	}, [])

	const allPlaceholders = useMemo(
		() => [
			{ key: "platformName", value: platform.name },
			{ key: "primaryColor", value: platform.primaryColor.value },
			...placeholders,
		],
		[placeholders, platform.name, platform.primaryColor.value],
	)

	// Kept unsubstituted so the test send can carry it as it is: the API substitutes there, and it is what
	// renders the editor document anyway.
	const rawContent = useMemo((): EmailContent | null => {
		if (selectedOptionId === "default") {
			if (!email.default) return null
			return {
				title: email.default.title,
				content: { type: "editor", document: email.default.document, html: email.default.html },
			}
		}
		if (isLegacyLocked && legacyContent) {
			return {
				title: email.custom?.title ?? "",
				content:
					legacyContent.type === "mjml"
						? { type: "mjml", value: layout.replace("{content}", legacyContent.value) }
						: { type: "html", value: legacyContent.value },
			}
		}
		// Keeps the preview panel in place while the editor is still empty instead of dropping it entirely.
		return {
			title: customTitle.trim(),
			content: {
				type: "editor",
				document: effectiveValue?.document ?? { type: "doc" },
				html: effectiveValue?.html ?? "",
			},
		}
	}, [
		customTitle,
		effectiveValue,
		email.custom?.title,
		email.default,
		isLegacyLocked,
		layout,
		legacyContent,
		selectedOptionId,
	])

	const previewContent = useMemo((): EmailContent | null => {
		if (!rawContent) return null
		const substitute = (text: string) => replacePlaceholders({ text, placeholders: allPlaceholders })

		return {
			title: substitute(rawContent.title),
			content:
				rawContent.content.type === "editor"
					? { ...rawContent.content, html: substitute(rawContent.content.html) }
					: { ...rawContent.content, value: substitute(rawContent.content.value) },
		}
	}, [allPlaceholders, rawContent])

	const switchToEditor = useCallback(() => {
		editorRevision.current += 1
		setHasSwitchedFromLegacy(true)
		setEditorValue(null)
		setIsEditorDirty(true)
	}, [])

	const handleGenerated = useCallback(
		(result: { title: string; document: StudentEmailEditorDocument; generationId: number }) => {
			editorRevision.current += 1
			setGenerated({ id: result.generationId, document: result.document })
			setCustomTitle(result.title)
			setSelectedOptionId("custom")
			setEditorValue(null)
			setIsEditorDirty(true)
			// The generated draft replaces the legacy email in the editor; the old content stays on the row
			// until the draft is saved.
			setHasSwitchedFromLegacy(true)
		},
		[],
	)

	const canUseAi = aiTarget !== undefined && platform.enabledFeatures.includes("ai-generated-courses")

	useEffect(() => {
		const isModified =
			selectedOptionId !== savedOptionId ||
			(selectedOptionId === "custom" &&
				!isLegacyLocked &&
				(customTitle.trim() !== (email.custom?.title ?? "") || isEditorDirty))

		// Legacy content is only unsaveable while it is still selected: `isModified` already excludes that
		// case, so reverting a legacy email to the default stays possible.
		if (!isModified) {
			toast.clearUnsavedChanges()
			return
		}

		toast.setUnsavedChanges(async () => {
			try {
				if (selectedOptionId === "default") {
					await onSave(null)
					return true
				}
				if (customTitle.trim().length === 0) {
					await alert.show("Missing subject", "Please enter a subject for the email.")
					return false
				}
				if (!effectiveValue || effectiveValue.html.trim().length === 0) {
					await alert.show("Empty email", "Please write some content before saving.")
					return false
				}
				const savedRevision = editorRevision.current
				await onSave({ title: customTitle.trim(), content: effectiveValue })
				// Keep edits made while the save was in flight marked as unsaved.
				if (editorRevision.current === savedRevision) setIsEditorDirty(false)
				return true
			} catch (error) {
				console.error(error)
				await alert.show("Error", "Failed to save email. Please try again.")
				return false
			}
		})
	}, [
		alert,
		customTitle,
		editorValue,
		effectiveValue,
		email.custom,
		isEditorDirty,
		isLegacyLocked,
		onSave,
		savedOptionId,
		selectedOptionId,
		toast,
	])

	return (
		<div className="flex flex-col items-start gap-4">
			<div className="flex w-full flex-wrap items-center justify-between gap-3">
				{email.default ? (
					<RadioButtons
						options={options.map(x => ({ value: x.id, label: x.label }))}
						value={selectedOptionId}
						onChange={value => setSelectedOptionId(options.find(x => x.id === value)?.id ?? "default")}
					/>
				) : (
					<span />
				)}
				<div className="flex items-center gap-2">
					{canUseAi && (
						<Button variant="secondary" onClick={() => setIsAiModalOpen(true)}>
							<IconSparkles className="size-4" />
							Write with Forento AI
						</Button>
					)}
					{previewContent && (
						<Button variant="secondary" onClick={() => setIsPreviewOpen(true)}>
							Preview
						</Button>
					)}
				</div>
			</div>
			{aiTarget && (
				<AiStudentEmailModal
					isOpen={isAiModalOpen}
					email={aiTarget}
					onClose={() => setIsAiModalOpen(false)}
					onGenerated={handleGenerated}
				/>
			)}
			<PreviewModal
				isOpen={isPreviewOpen}
				content={previewContent}
				rawContent={rawContent}
				placeholders={allPlaceholders}
				onClose={() => setIsPreviewOpen(false)}
			/>
			{selectedOptionId === "custom" &&
				(isLegacyLocked && legacyContent ? (
					<LegacyContent content={legacyContent} onSwitch={switchToEditor} />
				) : (
					<>
						<InputField
							className="self-stretch"
							label="Subject"
							value={customTitle}
							onChange={setCustomTitle}
						/>
						<StudentEmailEditor
							// Remounted for each generated draft, since the editor only reads content on mount.
							key={generated?.id ?? 0}
							initialValue={initialDocument}
							// primaryColor is still substituted for default and legacy MJML, where it filled a
							// colour attribute, but inserting a raw hex string into body text helps nobody.
							placeholders={allPlaceholders.map(x => x.key).filter(key => key !== "primaryColor")}
							primaryColor={platform.primaryColor.value}
							onChange={handleEditorChange}
						/>
						{generated && (
							<AiGenerationFeedback key={generated.id} id={generated.id} type="student-email" />
						)}
					</>
				))}
		</div>
	)
}

/**
 * The canvas already renders the email's own background, card and buttons, so a permanent preview column
 * would only repeat it while taking a third of the page. It lives behind a button instead, for the things the
 * canvas cannot show: the subject line in place, and a test send to a real inbox.
 */
type PreviewModalProps = {
	isOpen: boolean
	content: EmailContent | null
	/** The same email before substitution, which is what the test send carries. */
	rawContent: EmailContent | null
	placeholders: { key: string; value: string }[]
	onClose(): void
}
const PreviewModal: FC<PreviewModalProps> = ({ isOpen, content, rawContent, placeholders, onClose }) => {
	const toast = useToast()

	const sendTestEmail = query.studentEmails.sendTestEmail.useMutation({
		onSuccess() {
			toast.showSuccess("A test email has been sent to your email address")
		},
		onError() {
			toast.showError("Failed to send test email")
		},
	})

	return (
		// Elevated because the editor behind it puts its own floating toolbars at z-index: 50.
		<DefaultModal onClose={onClose} isOpen={isOpen} isElevated width={720}>
			<ModalTitle className="shrink-0">Preview</ModalTitle>
			<ModalText className="shrink-0">
				How the email looks when the student receives it, with the details you entered.
			</ModalText>
			{content && <EmailPreview {...content} />}
			<ModalButtons className="mt-6 shrink-0">
				<Button variant="secondary" onClick={onClose}>
					Close
				</Button>
				<Button
					variant="primary"
					isDisabled={!content}
					isLoading={sendTestEmail.isPending}
					onClick={() => {
						if (!rawContent) return
						sendTestEmail.mutate({
							title: rawContent.title,
							// The API renders an editor document itself and never takes HTML from here, so the
							// preview values travel with it and are substituted there instead.
							content:
								rawContent.content.type === "editor"
									? { type: "editor", document: rawContent.content.document }
									: rawContent.content,
							placeholders: Object.fromEntries(placeholders.map(x => [x.key, x.value])),
						})
					}}
				>
					Send test email
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

type LegacyContentProps = { content: LegacyStudentEmailContent; onSwitch(): void }
const LegacyContent: FC<LegacyContentProps> = ({ content, onSwitch }) => (
	<>
		<div className="border-component-border bg-component-background flex flex-col items-start gap-3 self-stretch rounded-xl border p-4">
			<p>
				This email was written in the old {content.type === "mjml" ? "MJML" : "HTML"} editor, which has been
				replaced. It keeps sending exactly as it is, and you can preview it here, but it can no longer be
				edited.
			</p>
			<p>
				To change it, switch to the new editor. Your current content stays below until you save, so you can copy
				anything you want to keep.
			</p>
			<Button variant="primary" onClick={onSwitch}>
				Switch to the new editor
			</Button>
		</div>
		<CodeEditor
			label={content.type === "mjml" ? "MJML content (read-only)" : "HTML content (read-only)"}
			defaultValue={content.value}
			isReadOnly
		/>
	</>
)

export default EditStudentEmail
